import { usePreferencesStore } from '@/stores/preferences';

interface WindowWithWebkitAudio extends Window {
    webkitAudioContext?: typeof AudioContext;
}

let audioCtx: AudioContext | null = null;

function getContext(): AudioContext {
    if (!audioCtx) {
        const Ctor = window.AudioContext ?? (window as WindowWithWebkitAudio).webkitAudioContext;
        if (!Ctor) {
            throw new Error('Web Audio API is not supported in this browser.');
        }
        audioCtx = new Ctor();
    }
    if (audioCtx.state === 'suspended') {
        void audioCtx.resume();
    }

    return audioCtx;
}

type OscillatorType = 'sine' | 'square' | 'triangle' | 'sawtooth';

function playTone(
    frequency: number,
    duration: number,
    type: OscillatorType = 'sine',
    volume = 0.15,
): void {
    const ctx = getContext();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = type;
    osc.frequency.setValueAtTime(frequency, ctx.currentTime);
    gain.gain.setValueAtTime(volume, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);

    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + duration);
}

export function useSoundEffects() {
    const preferences = usePreferencesStore();

    function playVote(): void {
        if (!preferences.soundEnabled) {
            return;
        }
        // Quick card flip: short high-pitched tick
        playTone(800, 0.08, 'square', 0.08);
    }

    function playReveal(): void {
        if (!preferences.soundEnabled) {
            return;
        }
        // Reveal swoosh: ascending two-tone
        playTone(400, 0.15, 'sine', 0.12);
        setTimeout(() => playTone(600, 0.2, 'sine', 0.1), 100);
    }

    function playJoin(): void {
        if (!preferences.soundEnabled) {
            return;
        }
        // Soft chime: gentle high note
        playTone(1200, 0.15, 'sine', 0.06);
    }

    function playEmoji(): void {
        if (!preferences.soundEnabled) {
            return;
        }
        // Pop: quick burst
        playTone(500, 0.06, 'triangle', 0.1);
    }

    function playTeamSessionCreated(): void {
        if (!preferences.soundEnabled) {
            return;
        }
        // Distinct three-note rising alert, warmer (triangle) than the others.
        playTone(660, 0.14, 'triangle', 0.1);
        setTimeout(() => playTone(880, 0.14, 'triangle', 0.1), 130);
        setTimeout(() => playTone(990, 0.22, 'triangle', 0.09), 270);
    }

    function playTeamInvited(): void {
        if (!preferences.soundEnabled) {
            return;
        }
        // Friendly two-note "ding-dong" for invitations.
        playTone(880, 0.14, 'sine', 0.1);
        setTimeout(() => playTone(1175, 0.2, 'sine', 0.09), 130);
    }

    return {
        playVote,
        playReveal,
        playJoin,
        playEmoji,
        playTeamSessionCreated,
        playTeamInvited,
    };
}
